import React, { useState } from "react";
import TeamService from "../services/teamService";

const EditTeamMemberForm = ({ member, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: member.name,
    role: member.role,
  });
  const [passwordData, setPasswordData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    // Validate password fields if section is open and filled
    if (showPasswordSection && passwordData.newPassword) {
      if (passwordData.newPassword.length < 8) {
        setErrors({ newPassword: "Password must be at least 8 characters" });
        setIsSubmitting(false);
        return;
      }
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setErrors({ confirmPassword: "Passwords do not match" });
        setIsSubmitting(false);
        return;
      }
    }

    try {
      const payload = { ...formData };

      // Only include password if the user filled it in
      if (showPasswordSection && passwordData.newPassword) {
        payload.newPassword = passwordData.newPassword;
      }

      const response = await TeamService.updateTeamMember(member.id, payload);
      onSuccess(response.data);
      onClose();
    } catch (error) {
      if (error.response && error.response.data.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ general: error.message || "An error occurred" });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Edit Team Member</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            &times;
          </button>
        </div>

        {errors.general && (
          <div className="mb-4 text-red-500 text-sm">{errors.general}</div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-md"
              required
            />
            {errors.name && <div className="text-red-500 text-sm mt-1">{errors.name}</div>}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={member.email}
              className="w-full px-3 py-2 border rounded-md bg-gray-100"
              readOnly
              disabled
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-md"
              required
            >
              <option value="Admin">Admin</option>
              <option value="Manager">Manager</option>
              <option value="Team Member">Team Member</option>
              <option value="Support">Support</option>
            </select>
            {errors.role && <div className="text-red-500 text-sm mt-1">{errors.role}</div>}
          </div>

          {/* Reset Password Section */}
          <div className="mb-4 border-t pt-4">
            <button
              type="button"
              onClick={() => {
                setShowPasswordSection((prev) => !prev);
                setPasswordData({ newPassword: "", confirmPassword: "" });
                setErrors((prev) => ({ ...prev, newPassword: undefined, confirmPassword: undefined }));
              }}
              className="flex items-center justify-between w-full text-sm font-medium text-gray-700"
            >
              <span>Reset password</span>
              <span className="text-gray-400">{showPasswordSection ? "▲" : "▼"}</span>
            </button>

            {showPasswordSection && (
              <div className="mt-3 space-y-3">
                <p className="text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
                  This will immediately update the member's password. Share it with them securely.
                </p>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New password
                  </label>
                  <input
                    type="password"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    placeholder="Min. 8 characters"
                    className="w-full px-3 py-2 border rounded-md"
                  />
                  {errors.newPassword && (
                    <div className="text-red-500 text-sm mt-1">{errors.newPassword}</div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm password
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    placeholder="Repeat new password"
                    className="w-full px-3 py-2 border rounded-md"
                  />
                  {errors.confirmPassword && (
                    <div className="text-red-500 text-sm mt-1">{errors.confirmPassword}</div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-md hover:bg-gray-50"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTeamMemberForm;